import React from 'react'
import { ExportOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Alert, Button, Modal, Space, Table, Tag, Typography } from 'antd'
import { ColumnsType, TableProps } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { transactionsApi } from 'api/transaction'
import { withdrawalRequestsApi } from 'api/withdrawalRequests'
import { ERoles } from 'models/auth'
import {
  ITransaction,
  TWithdrawalTransactionStatus
} from 'models/transaction'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { roundToKopecks } from 'shared/utils/roundToKopecks'
import { showServerError } from 'shared/utils/showServerError'
import { getWithdrawalTransactionStatusColor, getWithdrawalTransactionStatusLabel } from 'shared/constants/withdrawals'
import { showMessage } from 'shared/utils/showMessage'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { getDeclination } from 'shared/utils/getDeclination'
import { appSelectors } from 'store/app'
import { getSellerDataError } from '../utils/sellerDataErrors'
import styles from './WithdrawalRequestModal.module.scss'

interface IWithdrawalRequestModalProps {
  sellerId: string
  availableBalance: number
  withdrawalRequested: number
  pendingWithdrawal: number
  retainedBalance: number
  autoWithdrawal: boolean
  isOpen: boolean
  onSuccess: () => void
  close: () => void
}

const WITHDRAWAL_TRANSACTION_STATUSES: TWithdrawalTransactionStatus[] = [
  'Готов к выводу',
  'Заявка на вывод',
  'Ожидает вывода',
  'Ожидает 7 дней',
  'Выведено'
]

const READY_TO_WITHDRAW_STATUS: TWithdrawalTransactionStatus = 'Готов к выводу'
const PAGE_SIZE = 25

// getDeclination различает 11–14 только в пределах 5–20, поэтому передаём остаток от 100
const formatRublesAndKopecks = (sum: number) => {
  const totalKopecks = Math.round(sum * 100)
  const rubles = Math.floor(totalKopecks / 100)
  const kopecks = String(totalKopecks % 100).padStart(2, '0')
  const rublesLabel = getDeclination(rubles % 100, ['рублей', 'рубль', 'рубля'])

  return `${rubles.toLocaleString('ru-RU')} ${rublesLabel} ${kopecks} коп`
}

const showPenaltyCommissionModal = (penaltyCommissionSum: number) => {
  Modal.info({
    centered: true,
    title: `Вы могли бы заработать больше на ${formatRublesAndKopecks(penaltyCommissionSum)}`,
    icon: <InfoCircleOutlined />,
    okText: 'Понятно'
  })
}

export const WithdrawalRequestModal: React.FC<IWithdrawalRequestModalProps> = ({
  sellerId,
  availableBalance,
  withdrawalRequested,
  pendingWithdrawal,
  retainedBalance,
  autoWithdrawal,
  isOpen,
  onSuccess,
  close
}) => {
  const isMobile = useIsMobile()
  const role = useAppSelector(appSelectors.selectRole)

  const navigate = useNavigate()

  const [isLoading, setIsLoading] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [transactions, setTransactions] = React.useState<ITransaction[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])
  const [total, setTotal] = React.useState(0)
  const loaderRef = React.useRef<HTMLDivElement | null>(null)

  const getTransactions = React.useCallback(async (skip: number) => {
    try {
      setIsLoading(true)
      const response = await transactionsApi.getTransactions({
        skip: String(skip),
        limit: String(PAGE_SIZE),
        seller_id: sellerId,
        status: WITHDRAWAL_TRANSACTION_STATUSES
      })
      setTransactions((prev) => (
        skip === 0
          ? response.data.transactions
          : [...prev, ...response.data.transactions]
      ))
      setTotal(response.data.count)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }, [sellerId])

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    setTransactions([])
    setTotal(0)
    setSelectedRowKeys([])
    getTransactions(0)
  }, [getTransactions, isOpen])

  React.useEffect(() => {
    if (!isOpen || !loaderRef.current) {
      return
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || isLoading || transactions.length >= total) {
        return
      }

      getTransactions(transactions.length)
    })

    observer.observe(loaderRef.current)

    return () => observer.disconnect()
  }, [getTransactions, isLoading, isOpen, total, transactions.length])

  const onClose = () => {
    close()
    setSelectedRowKeys([])
  }

  const selectedTransactions = React.useMemo(() => {
    return transactions.filter((item) => selectedRowKeys.includes(item._id))
  }, [selectedRowKeys, transactions])

  // Округляем: сумма дробных транзакций даёт double-хвост (63699.400000000001),
  // и бэкенд, складывающий те же суммы в другом порядке, считал заявку превышающей баланс
  const selectedSum = React.useMemo(() => {
    return roundToKopecks(
      selectedTransactions.reduce((acc, item) => acc + item.sum, 0)
    )
  }, [selectedTransactions])

  const toggleRow = (record: ITransaction) => {
    if (record.status !== READY_TO_WITHDRAW_STATUS) {
      return
    }

    setSelectedRowKeys((prev) => (
      prev.includes(record._id)
        ? prev.filter((key) => key !== record._id)
        : [...prev, record._id]
    ))
  }

  const rowSelection: TableProps<ITransaction>['rowSelection'] = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.status !== READY_TO_WITHDRAW_STATUS
    })
  }

  const onSubmit = async () => {
    try {
      setIsSubmitting(true)
      // seller_id обязателен для супер-админа: бэкенд берёт его из тела запроса,
      // а из токена — только для самого продавца
      const { data } = await withdrawalRequestsApi.create({
        seller_id: sellerId,
        sum: selectedSum,
        transaction_ids: selectedTransactions.map((item) => item._id)
      })
      showMessage('success', 'Заявка на вывод создана', '')
      onSuccess()
      onClose()
      if (role === ERoles.USER && data.penalty_commission_sum) {
        showPenaltyCommissionModal(data.penalty_commission_sum)
      }
    } catch (e) {
      const sellerDataError = getSellerDataError(e)
      if (sellerDataError) {
        showMessage('error', sellerDataError.message, '')
        return navigate(sellerDataError.route)
      }
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const columns: ColumnsType<ITransaction> = [
    {
      title: 'Номер заказа',
      dataIndex: 'order_number',
      render: (_, { order_number }) => (
        <Typography.Text>{order_number ?? '—'}</Typography.Text>
      )
    },
    {
      title: 'Сумма',
      dataIndex: 'sum',
      render: (_, { sum }) => (
        <Typography.Text>{getNormalizedPrice(sum)}</Typography.Text>
      )
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      render: (_, { createdAt }) => (
        <Typography.Text>{moment(createdAt).locale('ru').format('LL')}</Typography.Text>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (_, { status }) => (
        status
          ? <Tag color={getWithdrawalTransactionStatusColor(status)}>{getWithdrawalTransactionStatusLabel(status)}</Tag>
          : <Typography.Text>—</Typography.Text>
      )
    }
  ]

  const mobileColumns: ColumnsType<ITransaction> = [
    {
      title: 'Заказ',
      dataIndex: 'order_number',
      width: '52%',
      render: (_, { order_number, createdAt }) => (
        <div className={styles.mobileCell}>
          <span className={styles.mobileOrderNumber}>{order_number ?? '—'}</span>
          <span className={styles.mobileSubText}>
            {moment(createdAt).locale('ru').format('LL')}
          </span>
        </div>
      )
    },
    {
      title: 'Сумма',
      dataIndex: 'sum',
      width: '48%',
      render: (_, { sum, status }) => (
        <div className={styles.mobileSumCell}>
          <Typography.Text>{getNormalizedPrice(sum)}</Typography.Text>
          {status && (
            <Tag color={getWithdrawalTransactionStatusColor(status)}>
              {getWithdrawalTransactionStatusLabel(status)}
            </Tag>
          )}
        </div>
      )
    }
  ]

  return (
    <Modal
      open={isOpen}
      footer={null}
      onCancel={onClose}
      width={940}
      style={{ top: isMobile ? 12 : 40 }}
    >
      <div className={styles.content}>
        <Typography.Title level={5} className={styles.title}>
          <ExportOutlined className={styles.icon} />
          Заявка на вывод средств
        </Typography.Title>
        <div className={styles.balanceInfo}>
          <div className={styles.balanceInfoItem}>
            <Typography.Text className={styles.balanceInfoLabel}>
              Ожидает разблокировки
            </Typography.Text>
            <Typography.Title level={4} className={styles.balanceInfoValue}>
              {getNormalizedPrice(retainedBalance)}
            </Typography.Title>
          </div>
          <div className={styles.balanceInfoItem}>
            <Typography.Text className={styles.balanceInfoLabel}>
              Доступно для вывода
            </Typography.Text>
            <Typography.Title level={4} className={styles.balanceInfoValue}>
              {getNormalizedPrice(availableBalance)}
            </Typography.Title>
          </div>
          <div className={styles.balanceInfoItem}>
            <Typography.Text className={styles.balanceInfoLabel}>
              Заявка на вывод
            </Typography.Text>
            <Typography.Title level={4} className={styles.balanceInfoValue}>
              {getNormalizedPrice(withdrawalRequested)}
            </Typography.Title>
          </div>
          <div className={styles.balanceInfoItem}>
            <Typography.Text className={styles.balanceInfoLabel}>
              Ожидает вывода
            </Typography.Text>
            <Typography.Title level={4} className={styles.balanceInfoValue}>
              {getNormalizedPrice(pendingWithdrawal)}
            </Typography.Title>
          </div>
        </div>
        <Table
          rowSelection={autoWithdrawal ? undefined : rowSelection}
          rowKey={(item) => item._id}
          onRow={autoWithdrawal ? undefined : (record) => ({
            className:
              record.status === READY_TO_WITHDRAW_STATUS
                ? styles.selectableRow
                : undefined,
            // клик по самому чекбоксу обрабатывает antd, иначе выбор сразу снимется
            onClick: (event) => {
              if (
                (event.target as HTMLElement).closest('.ant-checkbox-wrapper')
              ) {
                return
              }
              toggleRow(record)
            }
          })}
          dataSource={transactions}
          loading={isLoading && !transactions.length}
          size={isMobile ? 'small' : 'middle'}
          scroll={isMobile ? undefined : { x: 760 }}
          tableLayout={isMobile ? 'fixed' : undefined}
          columns={isMobile ? mobileColumns : columns}
          pagination={false}
        />
        <div className={styles.actions}>
          {autoWithdrawal ? (
            <Alert
              type='warning'
              showIcon
              className={styles.autoWithdrawalAlert}
              message='В автоматическом режиме заявки на вывод создаются сразу, как только выплата становится доступна'
            />
          ) : (
            <Space direction='horizontal' size={8} wrap>
              {Boolean(selectedSum) && (
                <Typography.Text className={styles.selectedSum}>
                  Выбрано на вывод: {getNormalizedPrice(selectedSum)}
                </Typography.Text>
              )}
              <Button htmlType='button' onClick={onClose}>
                Отменить
              </Button>
              <Button
                htmlType='button'
                type='primary'
                loading={isSubmitting}
                disabled={!selectedRowKeys.length}
                onClick={onSubmit}
              >
                Вывести
              </Button>
            </Space>
          )}
        </div>
        <div ref={loaderRef} className={styles.loaderAnchor}>
          {isLoading && transactions.length > 0 && (
            <Typography.Text>Загрузка...</Typography.Text>
          )}
        </div>
      </div>
    </Modal>
  )
}

